import React, { useCallback, useEffect, useRef } from 'react';
import { Trash, Edit } from '@withjoy/joykit/icons';
import { Box, ButtonV2, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { DragIcon } from '@assets/icons';
import { useIsTouchDevice } from '@shared/utils/media/useIsTouchDevice';
import { useSortableItemContext } from '@shared/components/SortableList/context/SortableItem.context';
import { HotelTile, TILE_BORDER_RADIUS } from '@shared/components/HotelTile';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { DistanceFromVenue } from '@shared/components/DistanceFromVenue';
import { useMetricFromGeoCurrency } from '@shared/utils/hooks/useMetricFromCurrency';
import { DistanceStatistics } from '@shared/components/DistanceStatistics';
import { SwipeAction } from '@shared/components/SwipeAction';
import type { SwipeActionRef } from '@shared/components/SwipeAction';
import type { StayAreaTileProps } from './StayAreaTile.types';

export const StayAreaTile = React.memo<StayAreaTileProps>(({ stayArea, isDragActive, containerWidth, onEdit, onRemove }) => {
  const useMetric = useMetricFromGeoCurrency();
  const { tq } = useTranslation('stayAreasAdmin');
  const isTouchDevice = useIsTouchDevice();
  const { attributes, listeners, draggableNodeRef } = useSortableItemContext();
  const swipeRef = useRef<SwipeActionRef>(null);

  useEffect(() => {
    if (isDragActive) {
      swipeRef.current?.close();
    }
  }, [isDragActive]);

  const thumbnailUrl = stayArea.carouselPhotos[0]?.src ?? null;

  const handleClick = useCallback(() => onEdit(stayArea), [onEdit, stayArea]);
  const handleRemoveClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove(stayArea);
    },
    [onRemove, stayArea],
  );

  return (
    <Box
      position="relative"
      role="group"
    >
      {/* Drag Handle — outside SwipeAction to avoid overflow:hidden clipping; hidden until backend supports reordering (TRAV-2250) */}
      <Flex
        position="absolute"
        left={pxToRem(-24)}
        top="50%"
        transform="translateY(-50%)"
        height="100%"
        paddingY={6}
        visibility="hidden"
        cursor={isDragActive ? 'grabbing' : 'grab'}
        alignItems="center"
        justifyContent="center"
        {...attributes}
        {...listeners}
        ref={draggableNodeRef}
      >
        <DragIcon size={16} />
      </Flex>

      <SwipeAction
        ref={swipeRef}
        disabled={isDragActive}
        stayOpen={false}
        borderRadius={pxToRem(TILE_BORDER_RADIUS)}
      >
        <SwipeAction.Content>
          <HotelTile
            onClick={handleClick}
            containerWidth={containerWidth}
            backgroundColor="white"
            border="1px solid"
            borderColor="mono3"
            padding={0}
            minHeight={pxToRem(150)}
            alignItems="stretch"
            gap={4}
          >
            <HotelTile.Thumbnail
              src={thumbnailUrl}
              alt={stayArea.name}
            />

            <Flex
              flex={1}
              flexDirection="column"
              gap={3}
              minWidth={0}
              paddingY={3}
              paddingRight={3}
              justifyContent="space-between"
              alignSelf="stretch"
            >
              <Flex
                flexDirection="column"
                gap={pxToRem(6)}
                alignItems="flex-start"
              >
                {/* Best For Badge */}
                {stayArea.bestForLabel && <HotelTile.Badges items={[{ label: stayArea.bestForLabel.label }]} />}

                {/* Name */}
                <TextEllipsisWrapper
                  lineClamp={2}
                  typographyVariant="hed2"
                  color="mono14"
                >
                  {stayArea.name}
                </TextEllipsisWrapper>

                {/* Metadata row: distance + drive time */}
                <Flex
                  gap={4}
                  alignItems="center"
                >
                  <DistanceFromVenue
                    distances={stayArea.travelDistances}
                    size="sm"
                    useMetric={useMetric}
                  />
                  <DistanceStatistics
                    distances={stayArea.travelDistances}
                    fontSize={pxToRem(13)}
                  />
                </Flex>
              </Flex>

              {/* Description */}
              {stayArea.description && (
                <TextEllipsisWrapper
                  lineClamp={2}
                  typographyVariant="label2"
                  color="mono14"
                >
                  {stayArea.description}
                </TextEllipsisWrapper>
              )}
            </Flex>

            {/* Trash icon — non-touch devices only; touch devices use swipe-to-delete */}
            {!isTouchDevice && (
              <Flex
                position="absolute"
                right={pxToRem(8)}
                top="50%"
                transform="translateY(-50%)"
                width={pxToRem(24)}
                height={pxToRem(24)}
                alignItems="center"
                justifyContent="center"
                backgroundColor="white"
                borderRadius={pxToRem(4)}
              >
                <ButtonV2
                  variant="ghost"
                  intent="neutral"
                  size="sm"
                  onClick={handleRemoveClick}
                  aria-label={tq`Remove`}
                  display="none"
                  _groupHover={{ display: 'flex' }}
                  padding={0}
                  minWidth="auto"
                  height="auto"
                  color="mono10"
                  _hover={{ color: 'mono14', backgroundColor: 'transparent' }}
                >
                  <Trash size={16} />
                </ButtonV2>
              </Flex>
            )}
          </HotelTile>
        </SwipeAction.Content>
        <SwipeAction.Action
          intent="neutral"
          icon={<Edit size={16} />}
          onClick={handleClick}
          aria-label={tq`Edit`}
        >
          {tq`Edit`}
        </SwipeAction.Action>
        <SwipeAction.Action
          intent="destructive"
          icon={<Trash size={16} />}
          onClick={() => onRemove(stayArea)}
          aria-label={tq`Remove`}
        >
          {tq`Remove`}
        </SwipeAction.Action>
      </SwipeAction>
    </Box>
  );
});

StayAreaTile.displayName = 'StayAreaTile';
